import { useState } from "react";
import { useAdmin } from "./AdminContext";
import DOMPurify from "dompurify";
import { Plus, Trash2, Check, Pencil, Minus, Mail, Phone, MapPin, Globe, Eye } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

export function AdminSettings() {
  const {
    settings, setSettings, handleSaveSettings,
    rejectionReasons, newReasonText, setNewReasonText, editingReasonId, setEditingReasonId, editingReasonText, setEditingReasonText,
    handleAddRejectionReason, handleUpdateRejectionReason, handleDeleteRejectionReason,
    emailTemplates, updateEmailTemplate, previewEmailTemplate,
    API
  } = useAdmin();

  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewSubject, setPreviewSubject] = useState("");
  const [expandedTemplate, setExpandedTemplate] = useState(null);

  const statusColors = {
    received: "bg-[#FFD600] text-black",
    in_process: "bg-[#00E5FF] text-black",
    ready: "bg-green-400 text-black",
    shipped: "bg-[#FF3366] text-white",
    completed: "bg-green-500 text-white",
    rejected: "bg-red-500 text-white",
  };

  const statusLabels = {
    received: "Order Received",
    in_process: "In Process",
    ready: "Ready",
    shipped: "Shipped",
    completed: "Completed",
    rejected: "Rejected",
  };

  const handlePreview = async (templateId) => {
    const result = await previewEmailTemplate(templateId);
    if (result) {
      setPreviewHtml(result.html);
      setPreviewSubject(result.subject);
    }
  };

  return (
    <div>
      {/* Contact Info */}
      <div className="mb-8">
        <h2 className="font-heading text-2xl font-black uppercase" data-testid="contact-settings-title">Contact Information</h2>
        <p className="text-xs text-zinc-500 mt-1">This information will be displayed on the storefront Contact Us section.</p>
      </div>
      <div className="brutal-card p-8 space-y-6">
        <div><label className="font-heading font-bold text-xs uppercase tracking-wider block mb-2"><Phone size={12} className="inline mr-1" /> Phone</label><input className="brutal-input" placeholder="(555) 123-4567" value={settings.contact_phone || ""} onChange={e => setSettings({...settings, contact_phone: e.target.value})} data-testid="contact-phone-input" /></div>
        <div><label className="font-heading font-bold text-xs uppercase tracking-wider block mb-2"><Mail size={12} className="inline mr-1" /> Email</label><input className="brutal-input" placeholder="contact@burnonsite.com" value={settings.contact_email || ""} onChange={e => setSettings({...settings, contact_email: e.target.value})} data-testid="contact-email-input" /></div>
        <div><label className="font-heading font-bold text-xs uppercase tracking-wider block mb-2"><MapPin size={12} className="inline mr-1" /> Location</label><input className="brutal-input" placeholder="Meredith, NH" value={settings.contact_location || ""} onChange={e => setSettings({...settings, contact_location: e.target.value})} data-testid="contact-location-input" /></div>
        <div><label className="font-heading font-bold text-xs uppercase tracking-wider block mb-2"><Globe size={12} className="inline mr-1" /> Website</label><input className="brutal-input" placeholder="https://burnonsite.com" value={settings.contact_website || ""} onChange={e => setSettings({...settings, contact_website: e.target.value})} data-testid="contact-website-input" /></div>
        <div><label className="font-heading font-bold text-xs uppercase tracking-wider block mb-2">Social / Other</label><input className="brutal-input" placeholder="@burnonsite on Instagram" value={settings.contact_social || ""} onChange={e => setSettings({...settings, contact_social: e.target.value})} data-testid="contact-social-input" /></div>
        <button onClick={handleSaveSettings} className="brutal-btn text-sm flex items-center gap-2" data-testid="save-contact-btn"><Check size={14} strokeWidth={2.5} /> Save Contact Info</button>
      </div>

      {/* Email Templates */}
      <div className="mt-10">
        <h2 className="font-heading text-2xl font-black uppercase" data-testid="email-templates-title">Email Templates</h2>
        <p className="text-xs text-zinc-500 mt-1 mb-4">Customize the emails sent to customers when their order status changes. Use variables like <code className="bg-zinc-100 px-1 border border-zinc-300 text-[10px]">{"{{customer_name}}"}</code>, <code className="bg-zinc-100 px-1 border border-zinc-300 text-[10px]">{"{{order_id}}"}</code>, <code className="bg-zinc-100 px-1 border border-zinc-300 text-[10px]">{"{{product_name}}"}</code>, <code className="bg-zinc-100 px-1 border border-zinc-300 text-[10px]">{"{{total}}"}</code>, <code className="bg-zinc-100 px-1 border border-zinc-300 text-[10px]">{"{{tracking_url}}"}</code>, <code className="bg-zinc-100 px-1 border border-zinc-300 text-[10px]">{"{{rejection_reason}}"}</code></p>
        <div className="space-y-4" data-testid="email-templates-list">
          {emailTemplates.map(tpl => (
            <div key={tpl.id} className="brutal-card overflow-hidden" data-testid={`email-template-${tpl.status_trigger}`}>
              <div className="p-5 flex items-center justify-between cursor-pointer" onClick={() => setExpandedTemplate(expandedTemplate === tpl.id ? null : tpl.id)}>
                <div className="flex items-center gap-3">
                  <span className={`text-[10px] font-heading font-bold uppercase px-2 py-1 border-2 border-black ${statusColors[tpl.status_trigger] || "bg-zinc-200"}`}>{statusLabels[tpl.status_trigger] || tpl.status_trigger}</span>
                  <span className="text-sm font-heading font-bold">{tpl.subject}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={tpl.enabled}
                      onCheckedChange={(checked) => updateEmailTemplate(tpl.id, { enabled: checked })}
                      data-testid={`template-toggle-${tpl.status_trigger}`}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <Label className="text-xs font-heading font-bold uppercase">{tpl.enabled ? "On" : "Off"}</Label>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); handlePreview(tpl.id); }}
                    className="brutal-btn brutal-btn-outline text-xs px-3 py-1.5 flex items-center gap-1"
                    data-testid={`preview-template-${tpl.status_trigger}`}
                  >
                    <Eye size={12} strokeWidth={2.5} /> Preview
                  </button>
                </div>
              </div>
              {expandedTemplate === tpl.id && (
                <div className="border-t-2 border-black p-5 bg-zinc-50 space-y-4">
                  <div>
                    <label className="font-heading font-bold text-xs uppercase tracking-wider block mb-2">Subject Line</label>
                    <input
                      className="brutal-input w-full text-sm"
                      value={tpl.subject}
                      onChange={e => {
                        const updated = emailTemplates.map(t => t.id === tpl.id ? { ...t, subject: e.target.value } : t);
                        // Local update only - save on blur
                        const idx = emailTemplates.findIndex(t => t.id === tpl.id);
                        if (idx >= 0) emailTemplates[idx].subject = e.target.value;
                      }}
                      onBlur={e => updateEmailTemplate(tpl.id, { subject: e.target.value })}
                      data-testid={`template-subject-${tpl.status_trigger}`}
                    />
                  </div>
                  <div>
                    <label className="font-heading font-bold text-xs uppercase tracking-wider block mb-2">Email Body (HTML)</label>
                    <textarea
                      className="brutal-input w-full text-xs font-mono min-h-[200px] resize-y"
                      defaultValue={tpl.html_body}
                      onBlur={e => updateEmailTemplate(tpl.id, { html_body: e.target.value })}
                      data-testid={`template-body-${tpl.status_trigger}`}
                    />
                  </div>
                  <p className="text-[10px] text-zinc-400">Available variables: {"{{customer_name}}"}, {"{{order_id}}"}, {"{{product_name}}"}, {"{{total}}"}, {"{{tracking_url}}"}, {"{{rejection_reason}}"}, {"{{estimated_date}}"}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Email Preview Modal */}
      {previewHtml && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setPreviewHtml(null)}>
          <div className="bg-white border-2 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] max-w-lg w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="email-preview-modal">
            <div className="border-b-2 border-black p-4 flex items-center justify-between bg-zinc-50">
              <div>
                <p className="text-[10px] font-heading font-bold uppercase text-zinc-400">Email Preview</p>
                <p className="text-sm font-bold mt-1">{previewSubject}</p>
              </div>
              <button onClick={() => setPreviewHtml(null)} className="brutal-btn brutal-btn-outline text-xs px-3 py-1.5" data-testid="close-preview-btn">Close</button>
            </div>
            <div className="p-4" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewHtml) }} />
          </div>
        </div>
      )}

      {/* Rejection Reasons */}
      <div className="mt-10">
        <h2 className="font-heading text-2xl font-black uppercase" data-testid="rejection-reasons-title">Rejection Reasons</h2>
        <p className="text-xs text-zinc-500 mt-1 mb-4">These reasons appear in the dropdown when rejecting an order. The selected reason is emailed to the customer.</p>
        <div className="brutal-card p-6 space-y-4">
          {rejectionReasons.map(r => (
            <div key={r.id} className="flex items-center gap-3 group" data-testid={`rejection-reason-${r.id}`}>
              {editingReasonId === r.id ? (
                <>
                  <input className="brutal-input flex-1 text-sm" value={editingReasonText} onChange={e => setEditingReasonText(e.target.value)} onKeyDown={e => e.key === "Enter" && handleUpdateRejectionReason(r.id)} data-testid="edit-reason-input" />
                  <button onClick={() => handleUpdateRejectionReason(r.id)} className="brutal-btn text-xs px-3 py-1.5" data-testid="save-reason-btn"><Check size={12} strokeWidth={2.5} /></button>
                  <button onClick={() => { setEditingReasonId(null); setEditingReasonText(""); }} className="brutal-btn brutal-btn-outline text-xs px-3 py-1.5"><Minus size={12} strokeWidth={2.5} /></button>
                </>
              ) : (
                <>
                  <span className="flex-1 text-sm text-zinc-700">{r.text}</span>
                  <button onClick={() => { setEditingReasonId(r.id); setEditingReasonText(r.text); }} className="opacity-0 group-hover:opacity-100 text-xs text-zinc-400 hover:text-black transition-opacity px-2 py-1 border border-zinc-300" data-testid={`edit-reason-${r.id}`}>Edit</button>
                  <button onClick={() => handleDeleteRejectionReason(r.id)} className="opacity-0 group-hover:opacity-100 text-xs text-red-400 hover:text-red-600 transition-opacity px-2 py-1 border border-red-300" data-testid={`delete-reason-${r.id}`}><Trash2 size={12} /></button>
                </>
              )}
            </div>
          ))}
          <div className="flex items-center gap-3 pt-3 border-t-2 border-dashed border-zinc-200">
            <input className="brutal-input flex-1 text-sm" placeholder="Add a new rejection reason..." value={newReasonText} onChange={e => setNewReasonText(e.target.value)} onKeyDown={e => e.key === "Enter" && handleAddRejectionReason()} data-testid="new-reason-input" />
            <button onClick={handleAddRejectionReason} disabled={!newReasonText.trim()} className="brutal-btn text-xs px-4 py-2 disabled:opacity-50" data-testid="add-reason-btn"><Plus size={12} className="inline mr-1" strokeWidth={2.5} /> Add</button>
          </div>
        </div>
      </div>
    </div>
  );
}
